import { getChartTitle } from '@/shared/copy/charts';
import type { MetricThresholdGroupKey } from '@/shared/utils/metricsThresholds';

type MetricInfoKey = 'totalRuns' | MetricThresholdGroupKey;

type CalculationSegment = 'empty' | 'failed' | 'passed' | 'skipped';
type CalculationBlock = CalculationSegment | CalculationSegment[];
type FormulaToken =
  | { kind: 'fraction'; denominator: string; numerator: string }
  | { kind: 'result'; offset?: 'down' | 'up'; value: string }
  | { kind: 'text'; offset?: 'down' | 'up'; value: string };
type FormulaExpression = {
  ariaLabel: string;
  tokens: FormulaToken[];
};

type MetricInfoCopy = {
  title: string;
  definition: string;
  secondaryDefinition?: string;
  formula: FormulaExpression;
  calculationBlockColumnSpan?: number;
  calculationSegments: CalculationBlock[];
  calculationSegmentLabels?: string[];
  calculationResult: FormulaExpression;
  calculationResultValue?: number;
};

const metricInfoCopy: Record<MetricInfoKey, MetricInfoCopy> = {
  totalRuns: {
    title: 'Total Runs',
    definition: 'The number of runs in the selected range.',
    formula: {
      ariaLabel: 'passed plus failed plus errored plus skipped plus untested equals total runs',
      tokens: [
        { kind: 'text', value: 'passed + failed + errored + skipped + untested' },
        { kind: 'text', value: '= total runs' },
      ],
    },
    calculationSegments: [
      'passed',
      'failed',
      'passed',
      'skipped',
      'passed',
      'passed',
      'failed',
      'passed',
      'passed',
      'passed',
    ],
    calculationResult: {
      ariaLabel: '10 runs equals 10 total',
      tokens: [{ kind: 'text', value: '10 runs = 10 total' }],
    },
  },
  passRate: {
    title: getChartTitle('passRate'),
    definition: 'The percentage of runs with a passed status.',
    secondaryDefinition:
      'Signals stability. Lower rates indicate more failing runs. Higher rates indicate stronger pass consistency.',
    formula: {
      ariaLabel:
        'open parenthesis passed divided by total close parenthesis times 100 equals success rate',
      tokens: [
        { kind: 'text', value: '(' },
        { kind: 'fraction', numerator: 'passed', denominator: 'total' },
        { kind: 'text', value: ')' },
        { kind: 'text', value: 'x 100 = success rate' },
      ],
    },
    calculationSegments: [
      'passed',
      'passed',
      'failed',
      'passed',
      'passed',
      'passed',
      'skipped',
      'passed',
      'failed',
      'passed',
    ],
    calculationResult: {
      ariaLabel:
        'open parenthesis 8 passed divided by 10 total close parenthesis times 100 equals 80 percent',
      tokens: [
        { kind: 'text', value: '(' },
        { kind: 'fraction', numerator: '8 passed', denominator: '10 total' },
        { kind: 'text', value: ')' },
        { kind: 'text', value: 'x 100 =' },
        { kind: 'result', value: '80%' },
      ],
    },
    calculationResultValue: 80,
  },
  frequency: {
    title: getChartTitle('frequency'),
    definition: 'The average number of runs over a period of time.',
    secondaryDefinition:
      'Signals confidence freshness. Lower frequency indicates fewer recent runs. Higher frequency indicates more frequent validation.',
    formula: {
      ariaLabel: 'total runs divided by days in range equals frequency',
      tokens: [
        { kind: 'fraction', numerator: 'total runs', denominator: 'days in range' },
        { kind: 'text', value: '= frequency' },
      ],
    },
    calculationBlockColumnSpan: 2,
    calculationSegments: [
      ['passed', 'passed'],
      ['failed', 'passed'],
      ['passed', 'failed'],
      ['passed', 'passed'],
      ['passed', 'skipped'],
    ],
    calculationResult: {
      ariaLabel: '10 runs divided by 5 days equals 2 per day',
      tokens: [
        { kind: 'fraction', numerator: '10 runs', denominator: '5 days' },
        { kind: 'text', value: '=' },
        { kind: 'result', offset: 'up', value: '2 / day' },
      ],
    },
    calculationResultValue: 2,
  },
  avgDuration: {
    title: 'Average Duration',
    definition: 'The mean duration for runs with duration data.',
    secondaryDefinition:
      'Signals execution conciseness and velocity. Lower duration indicates faster feedback. Higher duration indicates slower execution.',
    formula: {
      ariaLabel: 'total duration divided by runs with duration data equals average duration',
      tokens: [
        {
          kind: 'fraction',
          numerator: 'total duration',
          denominator: 'runs with duration data',
        },
        { kind: 'text', offset: 'down', value: '= average duration' },
      ],
    },
    calculationSegments: [
      'passed',
      'passed',
      'passed',
      'failed',
      'passed',
      'skipped',
      'passed',
      'passed',
      'failed',
      'passed',
    ],
    calculationSegmentLabels: ['2s', '2s', '1s', '3s', '2s', '', '2s', '2s', '3s', '1s'],
    calculationResult: {
      ariaLabel: 'total duration 18 seconds divided by 9 runs equals 2 seconds',
      tokens: [
        { kind: 'fraction', numerator: 'total duration 18s', denominator: '9 runs' },
        { kind: 'text', offset: 'down', value: '=' },
        { kind: 'result', offset: 'up', value: '2s' },
      ],
    },
    calculationResultValue: 2,
  },
  flakeScore: {
    title: getChartTitle('flakeScore'),
    definition: 'The percentage of pass / fail status changes between sequential runs.',
    secondaryDefinition:
      'Signals trust and consistency in execution outcomes. Lower rates indicate consistency. Higher rates indicate more status changes.',
    formula: {
      ariaLabel:
        'open parenthesis status changes divided by status transitions close parenthesis times 100 equals flake rate',
      tokens: [
        { kind: 'text', value: '(' },
        { kind: 'fraction', numerator: 'status changes', denominator: 'status transitions' },
        { kind: 'text', value: ')' },
        { kind: 'text', value: 'x 100 = flake rate' },
      ],
    },
    calculationSegments: [
      'passed',
      'passed',
      'failed',
      'passed',
      'passed',
      'failed',
      'failed',
      'passed',
      'passed',
      'passed',
    ],
    calculationResult: {
      ariaLabel:
        'open parenthesis 4 status changes divided by 9 transitions close parenthesis times 100 equals 44.4 percent',
      tokens: [
        { kind: 'text', value: '(' },
        { kind: 'fraction', numerator: '4 status changes', denominator: '9 transitions' },
        { kind: 'text', value: ')' },
        { kind: 'text', value: 'x 100 =' },
        { kind: 'result', value: '44.4%' },
      ],
    },
    calculationResultValue: 44.4,
  },
};

export { metricInfoCopy };
export type { CalculationBlock, CalculationSegment, FormulaExpression, MetricInfoKey };
